import { useAuthStore } from '@/stores/auth';
import type { UserRole } from '@/types/auth';

/**
 * Hook to check if the current user has a specific role
 *
 * @param role - The role to check
 * @returns boolean indicating if user has the role
 *
 * @example
 * ```tsx
 * const isAdmin = useRole('ADMIN');
 *
 * return (
 *   <div>
 *     {isAdmin && <AdminPanel />}
 *   </div>
 * );
 * ```
 */
export function useRole(role: UserRole): boolean {
  return useAuthStore((state) => state.checkRole(role));
}

/**
 * Hook to check if the current user has ANY of the specified roles
 *
 * @param roles - Array of roles to check
 * @returns boolean indicating if user has at least one role
 *
 * @example
 * ```tsx
 * const isAdminOrManager = useAnyRole(['ADMIN', 'SECURITY_MANAGER']);
 * ```
 */
export function useAnyRole(roles: UserRole[]): boolean {
  return useAuthStore((state) => state.checkAnyRole(roles));
}

/**
 * Hook to check if the current user has ALL of the specified roles
 *
 * @param roles - Array of roles to check
 * @returns boolean indicating if user has all roles
 *
 * @example
 * ```tsx
 * const hasMultipleRoles = useAllRoles(['ANALYST', 'OPERATOR']);
 * ```
 */
export function useAllRoles(roles: UserRole[]): boolean {
  return useAuthStore((state) => {
    const user = state.user;
    if (!user) {return false;}

    return roles.every((role) =>
      user.roles.some((userRole) => userRole.name === role)
    );
  });
}

/**
 * Hook to get all current user roles
 *
 * @returns Array of user roles or empty array if not authenticated
 *
 * @example
 * ```tsx
 * const roles = useRoles();
 * console.log('User roles:', roles);
 * ```
 */
export function useRoles() {
  return useAuthStore((state) => state.user?.roles ?? []);
}

/**
 * Hook to get the highest role of the current user
 * Based on role hierarchy: SUPER_ADMIN > ADMIN > SECURITY_MANAGER > ANALYST > OPERATOR > VIEWER
 *
 * @returns The highest role or null if not authenticated
 *
 * @example
 * ```tsx
 * const highestRole = useHighestRole();
 * console.log('Highest role:', highestRole);
 * ```
 */
export function useHighestRole(): UserRole | null {
  return useAuthStore((state) => {
    const user = state.user;
    if (!user?.roles.length) {return null;}

    const roleHierarchy: UserRole[] = [
      'SUPER_ADMIN',
      'ADMIN',
      'SECURITY_MANAGER',
      'ANALYST',
      'OPERATOR',
      'VIEWER',
    ];

    for (const role of roleHierarchy) {
      if (user.roles.some((userRole) => userRole.name === role)) {
        return role;
      }
    }

    return null;
  });
}
